import { useEffect, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import axios from "axios";
import { CheckCircle2, XCircle, Loader2, ArrowRight } from "lucide-react";
import { ASSETS } from "@/lib/api";
import { QRCodeCanvas } from "qrcode.react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function TicketSuccess() {
  const [params] = useSearchParams();
  const sessionId = params.get("session_id");
  const [status, setStatus] = useState({ state: "polling" });
  const [attempts, setAttempts] = useState(0);

  useEffect(() => {
    if (!sessionId) {
      setStatus({ state: "error", message: "Missing session_id in URL." });
      return;
    }
    let cancelled = false;
    async function poll() {
      try {
        const { data } = await axios.get(`${API}/payments/status/${sessionId}`);
        if (cancelled) return;
        if (data.payment_status === "paid") {
          setStatus({ state: "paid", data });
        } else if (["failed", "expired", "refunded"].includes(data.payment_status)) {
          setStatus({ state: "failed", data });
        } else if (attempts >= 15) {
          setStatus({ state: "timeout", data });
        } else {
          setAttempts((a) => a + 1);
          setTimeout(poll, 2000);
        }
      } catch (e) {
        if (attempts >= 5) {
          setStatus({ state: "error", message: "Could not confirm payment. Please contact the venue." });
        } else {
          setAttempts((a) => a + 1);
          setTimeout(poll, 2000);
        }
      }
    }
    poll();
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sessionId]);

  return (
    <div className="min-h-screen bg-[var(--trio-bg)] flex flex-col items-center justify-center px-6 py-16">
      <div className="w-full max-w-md text-center">
        <img src={ASSETS.logo} alt="Trio" className="h-14 w-auto mx-auto mb-6" />

        {status.state === "polling" && (
          <div data-testid="ticket-status-polling">
            <Loader2 className="w-12 h-12 mx-auto animate-spin text-[var(--trio-primary)] mb-4" />
            <div className="text-[11px] tracking-[0.3em] uppercase text-[var(--trio-primary)] font-bold mb-2">
              Confirming Payment
            </div>
            <h1 className="font-display uppercase text-3xl tracking-tight mb-2">Hold Tight…</h1>
            <p className="text-sm text-[var(--trio-muted)]">We're double-checking with Stripe.</p>
          </div>
        )}

        {status.state === "paid" && (
          <div data-testid="ticket-status-paid">
            <CheckCircle2 className="w-16 h-16 mx-auto text-green-600 mb-4" />
            <div className="text-[11px] tracking-[0.3em] uppercase text-green-700 font-bold mb-2">
              You're In
            </div>
            <h1 className="font-display uppercase text-4xl tracking-tight mb-3">Ticket Confirmed</h1>
            <p className="text-sm text-[var(--trio-muted)] mb-6">
              We just sent a confirmation to your email.
              {status.data?.event_title && (
                <> Your <span className="font-semibold text-black">{status.data.quantity}× ticket{status.data.quantity > 1 ? "s" : ""}</span> to <span className="font-semibold text-black">{status.data.event_title}</span> {status.data.quantity > 1 ? "are" : "is"} locked in.</>
              )}
            </p>
            {status.data?.ticket_id && (
              <div data-testid="ticket-qr-block" className="mb-6 border-2 border-dashed border-[var(--trio-primary)] p-5 bg-white">
                <div className="text-[11px] tracking-[0.3em] uppercase text-[var(--trio-primary)] font-bold mb-1">
                  Screenshot This Now
                </div>
                <p className="text-xs text-[var(--trio-muted)] mb-4">
                  This is your ticket. Show this QR code at the door to get in.
                </p>
                <QRCodeCanvas
                  data-testid="ticket-qr-code"
                  value={`${window.location.origin}/tickets/verify/${status.data.ticket_id}`}
                  size={200}
                  level="M"
                  includeMargin
                  className="mx-auto"
                />
                <div className="mt-3 text-sm font-semibold text-black">{status.data.buyer_name}</div>
                <div className="text-xs text-[var(--trio-muted)]">
                  {status.data.quantity}× · {status.data.event_title}
                </div>
                <div className="text-[10px] text-[var(--trio-muted)] mt-1 font-mono">
                  #{status.data.ticket_id.slice(0, 8).toUpperCase()}
                </div>
              </div>
            )}
            <Link
              to="/"
              data-testid="ticket-success-home"
              className="inline-flex items-center gap-2 bg-[var(--trio-primary)] hover:bg-[var(--trio-primary-hover)] text-white px-6 py-3 text-sm tracking-widest uppercase font-bold"
            >
              Back to Trio <ArrowRight className="w-4 h-4" />
            </Link>
          </div>
        )}

        {status.state === "failed" && (
          <div data-testid="ticket-status-failed">
            <XCircle className="w-16 h-16 mx-auto text-[var(--trio-primary)] mb-4" />
            <h1 className="font-display uppercase text-4xl tracking-tight mb-3">Payment Didn't Go Through</h1>
            <p className="text-sm text-[var(--trio-muted)] mb-6">
              Nothing was charged. Try again, or hit us up if you keep seeing this.
            </p>
            <Link to="/events" className="inline-flex items-center gap-2 border border-[var(--trio-text)] px-6 py-3 text-sm tracking-widest uppercase font-bold">
              Back to Events
            </Link>
          </div>
        )}

        {status.state === "timeout" && (
          <div data-testid="ticket-status-timeout">
            <Loader2 className="w-12 h-12 mx-auto text-[var(--trio-muted)] mb-4" />
            <h1 className="font-display uppercase text-3xl tracking-tight mb-3">Still Processing</h1>
            <p className="text-sm text-[var(--trio-muted)] mb-6">
              Stripe is taking longer than usual. Refresh in a minute, or check your email for a receipt.
            </p>
            <button
              onClick={() => window.location.reload()}
              className="inline-flex items-center gap-2 border border-[var(--trio-text)] px-6 py-3 text-sm tracking-widest uppercase font-bold"
            >
              Refresh
            </button>
          </div>
        )}

        {status.state === "error" && (
          <div data-testid="ticket-status-error">
            <XCircle className="w-16 h-16 mx-auto text-[var(--trio-primary)] mb-4" />
            <h1 className="font-display uppercase text-3xl tracking-tight mb-3">Something Went Sideways</h1>
            <p className="text-sm text-[var(--trio-muted)] mb-6">{status.message}</p>
            <Link to="/" className="inline-flex items-center gap-2 border border-[var(--trio-text)] px-6 py-3 text-sm tracking-widest uppercase font-bold">
              Home
            </Link>
          </div>
        )}
      </div>
    </div>
  );
}
